"use client";

import React, { CSSProperties, PropsWithChildren, useState } from "react";
import { Box, Button, Container, Typography, Dialog } from "@repo/ui";
import type { BoxProps } from "@repo/ui/Box/Box";
import clsx from "clsx";
import DownloadBrochureForm from "../forms/DownloadBrochureForm";
import { getCookie } from "cookies-next";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import Image, { ImageProps } from "next/image";

interface SectionProps {
  title?: string;
  paragraph?: string;
  actionLabel?: string;
  path?: string;
  className?: string;
  style?: CSSProperties;
  innerBoxProps?: Omit<BoxProps, "children">;
  id?: string;
  color?: "primary" | "secondary";
  bgImage?: string;
  bgImageProps?: Omit<ImageProps, "src" | "alt">;
}

const Section = ({
  title,
  paragraph,
  actionLabel,
  path,
  children,
  className,
  style,
  innerBoxProps,
  id,
  color,
  bgImage,
  bgImageProps,
  ...rest
}: PropsWithChildren<SectionProps>) => {
  const [open, setOpen] = useState(false);
  const POPUPS = ["COURSE_DOWNLOAD_BROCHURE"];
  const cookieValue = getCookie("serviceId");
  const serviceId =
    cookieValue !== undefined &&
    cookieValue !== "" &&
    !isNaN(Number(cookieValue))
      ? Number(cookieValue)
      : undefined;
  const onNavigate = () => {
    if (!path) return;

    if (POPUPS.includes(path ?? "")) {
      setOpen(true);
    }
  };

  const outlinedButtonContrastClasses =
    color === "primary"
      ? "!text-primary-contrastText !border-primary-contrastText hover:!bg-primary-contrastText hover:!text-primary-main hover:!border-primary-contrastText"
      : color === "secondary"
      ? "!text-gray-900 !border-gray-900 hover:!bg-gray-900 hover:!text-secondary-contrastText hover:!border-gray-900"
      : undefined;

  return (
    <Box
      className={clsx(className, {
        "bg-primary-main text-primary-contrastText group": color === "primary",
        "bg-secondary-main text-secondary-contrastText group":
          color === "secondary",
        "scroll-mt-[124px]": id !== undefined,
        "relative overflow-hidden w-full": Boolean(bgImage),
      })}
      render={<section />}
      id={id}
      style={style}
      data-color={color}
      {...rest}
    >
      {bgImage && (
        <Image
          src={bgImage}
          alt={id ?? "banned"}
          fill
          priority
          fetchPriority="high"
          sizes="100vw"
          quality={75}
          style={{ objectFit: "cover", objectPosition: "right" }}
          {...bgImageProps}
        />
      )}
      <Container className="relative">
        {Boolean(title) || Boolean(path) || Boolean(paragraph) ? (
          <>
            <Box
              {...innerBoxProps}
              className={clsx(
                "pt-[3.75rem] gap-4 grid sm:grid-flow-col grid-flow-row auto-cols-[1fr_auto]",
                innerBoxProps?.className
              )}
            >
              <Box className="mb-4 sm:mb-0">
                {Boolean(title) && (
                  <Typography
                    variant="h2"
                    className="font-bold [&>span]:group-data-[color=primary]:text-secondary-main [&>span]:group-data-[color=secondary]:text-primary-main [&>span]:text-primary-main"
                    gutterBottom={Boolean(paragraph)}
                  >
                    {title}
                  </Typography>
                )}

                {Boolean(paragraph) && (
                  <Typography paragraph>{paragraph}</Typography>
                )}
              </Box>

              {Boolean(path) && (
                <Button
                  onClick={POPUPS.includes(path ?? "") ? onNavigate : undefined}
                  render={
                    POPUPS.includes(path ?? "") ? (
                      <button />
                    ) : (
                      <Link href={path || "#"} />
                    )
                  }
                  color={color === "primary" ? "secondary" : "primary"}
                  variant="outlined"
                  roundedFull
                  fullWidth
                  label={actionLabel}
                  endIcon={<ArrowRight size={16} />}
                  className={outlinedButtonContrastClasses}
                />
              )}
            </Box>
            <Box
              {...innerBoxProps}
              className={clsx("mt-10 pb-[3.75rem]", innerBoxProps?.className)}
            >
              {children}
            </Box>
          </>
        ) : (
          <Box
            {...innerBoxProps}
            className={clsx("py-[3.75rem]", innerBoxProps?.className)}
          >
            {children}
          </Box>
        )}
      </Container>
      {/* Download course brochure popup */}
      <Dialog open={open} onOpenChange={setOpen} maxWidth="md" className="!p-0">
        <DownloadBrochureForm
          formType="COURSE_DOWNLOAD_BROCHURE"
          serviceId={serviceId}
          heading={
            actionLabel === "Download Curriculum"
              ? "Download Curriculum"
              : "Download Brochure"
          }
        />
      </Dialog>
    </Box>
  );
};

export default Section;
